import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Link from 'next/link';
import Separator from 'components/Separator';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/how-to-be-more-sustainable-this-christmas`;

  return (
    <MainLayout>
      <NextSeo
        title="How To Be More Sustainable This Christmas | Win Epic Prizes | DAYMADE"
        description="Want to be more sustainable this Christmas? Read our top tips on how to be more eco-friendly this festive season."
        openGraph={{
          title:
            'How To Be More Sustainable This Christmas | Win Epic Prizes | DAYMADE',
          description:
            'Want to be more sustainable this Christmas? Read our top tips on how to be more eco-friendly this festive season.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/how-to-be-more-sustainable-this-christmas',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Christmas presents and decorations spread out on table',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'Christmas,Christmas tree,environmentally friendly,eco friendly,sustainable,decorations,nkuku,etsy,fatface,biodegradable,seed card,ecovibe,eco vibe,curlicue,lush,festive,the Christmas forest,caring Christmas trees,Bethany Christian trust,tree aid,win a prize,win a gift,competition,sustainable Christmas,eco friendly Christmas,make eco friendly decorations',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Christmas presents and decorations spread out on table"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            How To Be More Sustainable This <strong>Christmas</strong>
          </h1>

          <p className="subheading">
            Our top tips for eco-friendly festivities
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Abbi James
          </p>

          <p>
            Christmas is without a doubt the time for celebrating with those you
            love. For many it’s also a time of excess – from electricity usage
            to a flood of plastic packaging. We’re not here to be Ebenezer
            Scrooge, Bah Humbug-ing our way into your festivities, but we do
            have a few tips to enjoy Christmas in a more sustainable and
            eco-friendly way!
          </p>

          <p>
            Each December, our homes are adorned with an abundance of Christmas
            decorations. The most eco-friendly decorations are those you already
            have, passed down through generations or kept from years past. But
            it’s only natural to want a refresh every now and then, to add a new
            twist each year!
          </p>

          <p>
            If you’re looking to buy decorations, choose ones made from recycled
            or sustainable materials. Local craft fairs are a great way to find
            unique pieces, or failing that, Etsy has an abundance of
            eco-friendly options. When choosing a brand to buy from, take a look
            at their views towards sustainability. nkuku is built on ethical,
            eco-friendly and handmade values, and offers a range of beautiful
            decorations for both the home and tree. Felted decorations are also
            a great option to bring some festive cheer, as they’re fully
            re-usable year after year. We’re big fans of FatFace’s Woodland Tree
            Decorations, made from felt by a brand set on strong sustainability
            goals. We’re such big fans in fact, we’ve added them as {''}
            {/* Commented out the link because this prize is hidden and clicking it gives a 404 error.
                We should find a better way to handle this though, as it's relatively common that
                a blog post will link to a prize that we no longer offer. */}
            {/*<Link href='/prizes/details/felted-christmas-tree-decorations'>*/}
            {/*  <a style={{ textDecoration: 'underline', whiteSpace: 'break-spaces' }}>*/}
            a £25 prize
            {/*  </a>*/}
            {/*</Link> */}
            {''} in the DAYMADE Weekly Draws!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="Sustainable decorations hanging on Christmas tree"
            className="singleBlogImage"
          />

          <p>
            If you’re feeling creative, you can upcycle existing decorations or
            create your own. Fill your home with festive scents with a homemade
            dried citrus and cinnamon garland, or a popcorn and dried cranberry
            garland to substitute for tinsel! DIY your own origami upcycled
            paper stars to hang on the Christmas tree, in your windows or as
            table place settings. And for those who have a garden or local park,
            you can create your own Christmas wreath from a misshapen metal coat
            hanger with fallen foliage, sprigs of rosemary and dried berries.
            However you decide to get creative, we recommend using plant-based,
            biodegradable glitter to add some sustainable sparkle to your
            crafts. Sparkle Town, Eco Glitter Fun and A Beautiful Weirdo all
            have stunning biodegradable glitter perfect for lighting up the
            festive season.
          </p>

          <p>
            One tradition that still goes strong is sending Christmas cards.
            Whilst the majority are made mostly from cardboard, many will still
            contain plastic-based glitter and cause environmental impact through
            the manufacturing process. We’re opting instead for making our own
            Christmas cards, whether they be upcycled from years past or created
            from scratch to let your creative juices flow. Last year’s cards can
            also be kept to repurpose for present tags or to be used towards
            making paper garlands to adorn your home with. If you’re in a pinch
            and need to buy a last-minute card, look out for biodegradable cards
            embedded with seeds from the likes of The Seed Card Company or
            EcoVibe, for the ultimate Christmas card that keeps on giving. We’re
            so fond of The Seed Card Company that we’ve added their {''}
            {/* Commented out the link because this prize is hidden and clicking it gives a 404 error.
                We should find a better way to handle this though, as it's relatively common that
                a blog post will link to a prize that we no longer offer. */}
            {/*<Link href='/prizes/details/seed-christmas-card'>*/}
            {/*  <a style={{ textDecoration: 'underline', whiteSpace: 'break-spaces' }}>*/}
            100% biodegradable seed-embedded cards
            {/*</a>*/}
            {/*</Link> */}
            {''} to our £5 prize category, so you can try them out for yourself!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="Sustainable Christmas card with Christmas Tree on front"
            className="singleBlogImage"
          />

          <p>
            Wrapping your specially-chosen gifts in an environmentally friendly
            way is much easier than you’d think. If you’re opting to buy your
            wrapping paper, go for one that’s preferably made from recycled
            materials and/or is even recyclable itself. Recycle Now have
            highlighted the ‘scrunch test’, whereby if paper is scrunched into a
            ball and holds its shape, it is most likely recyclable. Our
            favourite eco-friendly and sustainable wrapping papers are from
            EcoVibe and Curlicue, with beautiful designs to set your gift apart.
          </p>

          <p>
            If you’re looking to add a unique touch, opt for plantable seed
            wrapping paper, recyclable decorated boxes or brown kraft paper
            adorned with repurposed Christmas gift tags and handmade
            decorations. For presents that are a little fiddly to wrap, reusable
            and recycled fabric wrap is the perfect waste-free wrapping
            alternative. We’re big fans of Lush’s 100% organic cotton and 100%
            recycled plastic knot wraps, which can be used year after year for
            added brownie points.
          </p>

          <p>
            The Christmas tree has been a centrepiece of festive celebrations
            for centuries, with Germany being credited as starting this
            international tradition of bringing decorated trees into the home.
            The decision as to whether to have a real Christmas tree or a fake
            fir is down to personal preference, with many having strong opinions
            based on their family’s traditions through the years. If you already
            have a Christmas tree tucked away in the attic, the most sustainable
            option is of course to re-use it each year. If it’s starting to look
            a little tired, it can be spruced up with the addition of fallen
            foliage, homemade garlands and handmade decorations.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Family decoration Christmas Tree with decorations"
            className="singleBlogImage"
          />

          <p>
            For those looking to buy a real tree, ensure it is responsibly
            sourced and look out for the FSC-certified label. One of your most
            sustainable options is to purchase a potted tree that you can re-use
            each year and look after between seasons. If you don’t have the
            outdoor space to look after your festive friend year-round, various
            companies have started renting Christmas trees for you to ‘adopt’
            during the festive period before returning it to be replanted for
            next year. If you do find yourself going down the cut-tree route, we
            suggest buying from a brand focusing on sustainability. For those in
            Scotland and Edinburgh, we’d suggest buying your Christmas tree from{' '}
            {''}
            {/* Commented out the link because this prize is hidden and clicking it gives a 404 error.
                We should find a better way to handle this though, as it's relatively common that
                a blog post will link to a prize that we no longer offer. */}
            {/*<Link href='/prizes/details/6ft-nordmann-fir-christmas-tree'>*/}
            {/*  <a style={{ textDecoration: 'underline', whiteSpace: 'break-spaces' }}>*/}
            Bethany Christian Trust’s Caring Christmas Trees
            {/*  </a>*/}
            {/*</Link>*/}. They’re sustainably grown locally in Scotland, with
            100% of the proceeds going to support people facing homelessness and
            in vulnerable situations. If you’re located across the UK, however,
            we’re big fans of {''}
            {/* Commented out the link because this prize is hidden and clicking it gives a 404 error.
                We should find a better way to handle this though, as it's relatively common that
                a blog post will link to a prize that we no longer offer. */}
            {/*<Link href='/prizes/details/6ft-christmas-forest-tree'>*/}
            {/*  <a style={{ textDecoration: 'underline', whiteSpace: 'break-spaces' }}>*/}
            The Christmas Forest’s Nordmann Firs
            {/*  </a>*/}
            {/*</Link>*/}, which are grown with sustainability in mind and
            supporting their TREE AID collaboration. If you do find yourself
            with a cut tree, make sure you know how to dispose of it come
            January – will you recycle it to create a habitat for bugs and birds
            in your garden, or use the wood chippings for a local park or
            woodland?
          </p>

          <p>
            However you decide to celebrate this Christmas period, know that
            even small steps can make a difference. Choose your Christmas tree
            wisely and adorn it with handmade decorations and garlands made with
            family and friends. Wrap your eco-friendly gifts in recycled,
            repurposed and reusable materials for an added sentimental touch.
            Enjoy the start of a new tradition towards a more sustainable
            Christmas!
          </p>

          <Separator style={{ margin: '1.5em 0 2em' }} />

          <p style={{ textAlign: 'center' }}>
            Enter our weekly draw for your chance to win your sustainable
            Christmas tree, gifts and more!
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1.5em 0' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
